/**
 * Hook for subscribing to campaign WebSocket updates.
 * Uses DCRF streams with `campaign` stream and target campaign ID.
 */
import { useEffect, useCallback, useRef, useState } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { transformKeys } from '../services/transformers';
import type { Campaign } from '../types';

export type CampaignObserverEvent = Partial<Campaign> & {
  id: string;
  streamEventType?: string;
  changedSections?: string[];
  refreshReason?: string;
  importStatus?: 'idle' | 'processing' | 'completed' | 'failed';
  importPhase?: 'downloading' | 'parsing' | 'validating' | 'processing' | 'saving' | 'finalizing';
  importProgress?: number;
  importProcessedRows?: number;
  importTotalRows?: number;
  importMessage?: string;
  importError?: string;
  importStartedAt?: string | null;
  importCompletedAt?: string | null;
};

interface ImportProgress {
  progress: number;
  processedRows: number;
  totalRows: number;
  message?: string;
  estimatedRemainingSeconds?: number;
  phase?: 'downloading' | 'parsing' | 'validating' | 'processing' | 'saving' | 'finalizing';
}

interface UseCampaignWebSocketOptions {
  onStatusChange?: (event: CampaignObserverEvent) => void;
  onImportProgress?: (event: CampaignObserverEvent) => void;
  onImportComplete?: (event: CampaignObserverEvent) => void;
  onCampaignUpdated?: (event: CampaignObserverEvent) => void;
  onCampaignInvalidated?: (event: CampaignObserverEvent) => void;
}

interface UseCampaignWebSocketReturn {
  isConnected: boolean;
  isSubscribed: boolean;
  importProgress: ImportProgress | null;
  lastEvent: CampaignObserverEvent | null;
}

interface CampaignRuntimeEventEnvelope {
  eventType?: string;
  payload?: Partial<CampaignObserverEvent> & {
    id?: string;
    campaignId?: string;
    changedSections?: string[];
    refreshReason?: string;
  };
  scope?: {
    campaignId?: string;
  };
}

/**
 * Subscribe to campaign updates and import progress.
 */
export function useCampaignWebSocket(
  campaignId: string | undefined,
  options: UseCampaignWebSocketOptions = {},
): UseCampaignWebSocketReturn {
  const {
    onStatusChange,
    onImportProgress,
    onImportComplete,
    onCampaignUpdated,
    onCampaignInvalidated,
  } = options;

  const { state, subscribe } = useWebSocket();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [lastEvent, setLastEvent] = useState<CampaignObserverEvent | null>(null);
  const lastImportStatusRef = useRef<string | null>(null);
  const lastImportCompletedAtRef = useRef<string | null>(null);
  const isDebugEnabled = useCallback(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.has('campaignDebug') || window.localStorage.getItem('ct_campaign_debug') === '1';
  }, []);
  const logDebug = useCallback(
    (message: string, data?: unknown) => {
      if (!isDebugEnabled()) return;
      if (data !== undefined) {
        console.log(`[CampaignWS] ${message}`, data);
        return;
      }
      console.log(`[CampaignWS] ${message}`);
    },
    [isDebugEnabled],
  );
  const callbacksRef = useRef<UseCampaignWebSocketOptions>({
    onStatusChange,
    onImportProgress,
    onImportComplete,
    onCampaignUpdated,
    onCampaignInvalidated,
  });

  useEffect(() => {
    callbacksRef.current = {
      onStatusChange,
      onImportProgress,
      onImportComplete,
      onCampaignUpdated,
      onCampaignInvalidated,
    };
  }, [onStatusChange, onImportProgress, onImportComplete, onCampaignUpdated, onCampaignInvalidated]);

  const handleEvent = useCallback(
    (data: CampaignObserverEvent | CampaignRuntimeEventEnvelope, action: string) => {
      if (action === 'event') {
        const runtimeEnvelope = transformKeys<CampaignRuntimeEventEnvelope>(data);
        const runtimePayload = runtimeEnvelope.payload ?? {};
        const runtimeEventCampaignId = runtimePayload.id
          ?? runtimePayload.campaignId
          ?? runtimeEnvelope.scope?.campaignId
          ?? campaignId;

        if (!runtimeEventCampaignId) {
          return;
        }
        if (campaignId && runtimeEventCampaignId !== campaignId) {
          return;
        }

        const normalizedRuntimeEvent: CampaignObserverEvent = {
          ...(runtimePayload as CampaignObserverEvent),
          id: String(runtimeEventCampaignId),
          streamEventType: runtimeEnvelope.eventType,
          changedSections: Array.isArray(runtimePayload.changedSections)
            ? runtimePayload.changedSections
            : undefined,
          refreshReason: runtimePayload.refreshReason,
        };

        setLastEvent(normalizedRuntimeEvent);
        callbacksRef.current.onCampaignInvalidated?.(normalizedRuntimeEvent);
        return;
      }

      if (action !== 'update' && action !== 'create') {
        return;
      }

      const normalized = transformKeys<CampaignObserverEvent>(data);

      if (normalized.id && campaignId && normalized.id !== campaignId) {
        return;
      }

      setLastEvent(normalized);
      callbacksRef.current.onCampaignUpdated?.(normalized);
      callbacksRef.current.onStatusChange?.(normalized);

      const previousImportStatus = lastImportStatusRef.current;
      const currentImportStatus = normalized.importStatus;
      const previousImportCompletedAt = lastImportCompletedAtRef.current;
      const currentImportCompletedAt = normalized.importCompletedAt ?? null;
      logDebug('event received', {
        action,
        campaignId,
        eventId: normalized.id,
        previousImportStatus,
        currentImportStatus,
        previousImportCompletedAt,
        currentImportCompletedAt,
        raw: data,
        normalized,
      });

      if (normalized.importStatus === 'processing') {
        const rawImportStats = normalized.importStats as Record<string, unknown> | undefined;
        const estimatedRemainingSeconds = Number(
          rawImportStats?.estimatedRemainingSeconds
            ?? rawImportStats?.estimated_remaining_seconds,
        );
        setImportProgress({
          progress: normalized.importProgress ?? 0,
          processedRows: normalized.importProcessedRows ?? 0,
          totalRows: normalized.importTotalRows ?? 0,
          message: normalized.importMessage ?? undefined,
          estimatedRemainingSeconds: Number.isFinite(estimatedRemainingSeconds)
            ? estimatedRemainingSeconds
            : undefined,
          phase: normalized.importPhase as ImportProgress['phase'] | undefined,
        });
        callbacksRef.current.onImportProgress?.(normalized);
      } else if (normalized.importStatus === 'completed' || normalized.importStatus === 'failed') {
        setImportProgress(null);
        const isTransitionFromProcessing = previousImportStatus === 'processing';
        const hasNewCompletedAt =
          normalized.importStatus === 'completed' &&
          Boolean(currentImportCompletedAt) &&
          Boolean(previousImportCompletedAt) &&
          currentImportCompletedAt !== previousImportCompletedAt;
        const shouldNotifyImportComplete = isTransitionFromProcessing || hasNewCompletedAt;

        if (shouldNotifyImportComplete) {
          logDebug('onImportComplete firing', {
            previousImportStatus,
            currentImportStatus: normalized.importStatus,
            isTransitionFromProcessing,
            hasNewCompletedAt,
          });
          callbacksRef.current.onImportComplete?.(normalized);
        } else {
          logDebug('onImportComplete skipped (no valid transition)', {
            previousImportStatus,
            currentImportStatus: normalized.importStatus,
            isTransitionFromProcessing,
            hasNewCompletedAt,
          });
        }
      } else {
        setImportProgress(null);
      }
      if (currentImportStatus) {
        lastImportStatusRef.current = currentImportStatus;
        logDebug('import status ref updated', { currentImportStatus });
      }
      if (currentImportCompletedAt) {
        lastImportCompletedAtRef.current = currentImportCompletedAt;
        logDebug('import completed_at ref updated', { currentImportCompletedAt });
      }
    },
    [campaignId, logDebug],
  );

  useEffect(() => {
    if (!campaignId || !state.isConnected) {
      setIsSubscribed(false);
      return;
    }

    const subscription = subscribe('campaign', campaignId, handleEvent);
    setIsSubscribed(true);

    return () => {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
      setIsSubscribed(false);
    };
  }, [campaignId, state.isConnected, subscribe, handleEvent]);

  return {
    isConnected: state.isConnected,
    isSubscribed,
    importProgress,
    lastEvent,
  };
}

export default useCampaignWebSocket;
